import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCampaignYouTubeMedia, approveYouTubeMedia, getYouTubeMediaApprovalRequests } from '../../../influencer/services/affiliateService';
import { FaYoutube, FaEye, FaThumbsUp, FaComment, FaCheck, FaTimes, FaArrowLeft, FaFilter } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const FilterChip = ({ label, onRemove }) => (
  <div className="inline-flex items-center bg-gray-100 rounded-full px-3 py-1 mr-2 mb-2">
    <span className="text-sm text-gray-700">{label}</span>
    <button
      onClick={onRemove}
      className="ml-2 text-gray-500 hover:text-gray-700"
    >
      <FaTimes size={12} />
    </button>
  </div>
);

const YouTubeMediaApproval = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const campaignId = searchParams.get('campaignId');
  const affiliateId = searchParams.get('affiliateId');
  
  const [youtubeMedia, setYoutubeMedia] = useState(null);
  const [filteredMedia, setFilteredMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    mediaId: null,
    videoId: null,
    campaignId: null,
    action: null,
    rejectionReason: ''
  });
  // Apply filters whenever youtubeMedia changes
  useEffect(() => {
    if (!youtubeMedia) return;

    let filtered = { ...youtubeMedia };

    if (campaignId && affiliateId) {
      // Filter specific campaign/affiliate media
      if (youtubeMedia.videos && statusFilter !== 'all') {
        filtered.videos = youtubeMedia.videos.filter(video => 
          video.campaignStatus === statusFilter
        );
      }
    } else {
      // Filter approval requests
      if (youtubeMedia.approvalRequests && statusFilter !== 'all') {
        filtered.approvalRequests = youtubeMedia.approvalRequests.filter(request => 
          request.approvalRequest.status === statusFilter
        );
      }
    }

    setFilteredMedia(filtered);
  }, [youtubeMedia, statusFilter, campaignId, affiliateId]);

  const fetchYouTubeMedia = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCampaignYouTubeMedia(campaignId, affiliateId);
      
      if (response.success) {
        // Sort videos by creation date (latest first - stack structure)
        const sortedData = {
          ...response.data,
          videos: response.data.videos ? response.data.videos.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.publishedAt || 0);
            const dateB = new Date(b.createdAt || b.publishedAt || 0);
            return dateB - dateA;
          }) : []
        };
        setYoutubeMedia(sortedData);
        setFilteredMedia(sortedData);
        // if (response.data.videos && response.data.videos.length > 0) {
        //   toast.info(`Found ${response.data.videos.length} YouTube media items for review`, {
        //     position: "top-right",
        //     autoClose: 3000,
        //     hideProgressBar: false,
        //     closeOnClick: true,
        //     pauseOnHover: true,
        //     draggable: true,
        //   });
        // } else {
        //   toast.info('No YouTube media found for this affiliate', {
        //     position: "top-right",
        //     autoClose: 3000,
        //     hideProgressBar: false,
        //     closeOnClick: true,
        //     pauseOnHover: true,
        //     draggable: true,
        //   });
        // }
      } else {
        setError(response.message || 'Failed to load YouTube media');
        toast.error(response.message || 'Failed to load YouTube media', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (err) {
      setError('Failed to load YouTube media');
      toast.error('Failed to load YouTube media. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [campaignId, affiliateId]);

  const fetchApprovalRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getYouTubeMediaApprovalRequests();
      if (response.success) {
        // Sort approval requests by creation date (latest first - stack structure)
        const sortedData = {
          approvalRequests: response.data ? response.data.sort((a, b) => {
            const dateA = new Date(a.approvalRequest?.requestedAt || a.createdAt || 0);
            const dateB = new Date(b.approvalRequest?.requestedAt || b.createdAt || 0);
            return dateB - dateA;
          }) : []
        };
        setYoutubeMedia(sortedData);
        setFilteredMedia(sortedData);
        // if (response.data && response.data.length > 0) {
        //   toast.info(`Found ${response.data.length} YouTube media approval requests`, {
        //     position: "top-right",
        //     autoClose: 3000,
        //     hideProgressBar: false,
        //     closeOnClick: true,
        //     pauseOnHover: true,
        //     draggable: true,
        //   });
        // } else {
        //   toast.info('No pending YouTube media approval requests', {
        //     position: "top-right",
        //     autoClose: 3000,
        //     hideProgressBar: false,
        //     closeOnClick: true,
        //     pauseOnHover: true,
        //     draggable: true,
        //   });
        // }
      } else {
        setError(response.message || 'Failed to fetch approval requests');
        toast.error(response.message || 'Failed to fetch approval requests', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (err) {
      setError('Failed to fetch approval requests');
      toast.error('Failed to fetch approval requests. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (campaignId && affiliateId) {
      fetchYouTubeMedia();
    } else {
      fetchApprovalRequests();
    }
  }, [campaignId, affiliateId, fetchYouTubeMedia, fetchApprovalRequests]);
  

  const handleFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    
    // Update active filters
    const newFilters = [...activeFilters];
    const existingFilterIndex = newFilters.findIndex(f => f.type === 'status');
    
    if (existingFilterIndex >= 0) {
      if (newStatus === 'all') {
        newFilters.splice(existingFilterIndex, 1);
      } else {
        newFilters[existingFilterIndex].value = newStatus;
      }
    } else if (newStatus !== 'all') {
      newFilters.push({ type: 'status', value: newStatus });
    }
    
    setActiveFilters(newFilters);
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setActiveFilters([]);
  };

  const getStatusCounts = () => {
    if (!youtubeMedia) return { all: 0, pending: 0, approved: 0, rejected: 0 };
    
    const counts = { all: 0, pending: 0, approved: 0, rejected: 0 };
    
    if (campaignId && affiliateId && youtubeMedia.videos) {
      counts.all = youtubeMedia.videos.length;
      youtubeMedia.videos.forEach(video => {
        counts[video.campaignStatus || 'pending']++;
      });
    } else if (youtubeMedia.approvalRequests) {
      counts.all = youtubeMedia.approvalRequests.length;
      youtubeMedia.approvalRequests.forEach(request => {
        counts[request.approvalRequest.status || 'pending']++;
      });
    }
    
    return counts;
  };

  const statusCounts = getStatusCounts();

  const handleApprove = async () => {
    const { mediaId, videoId, campaignId, action, rejectionReason } = approvalModal;
    
    try {
      const response = await approveYouTubeMedia(mediaId, videoId, campaignId, action, rejectionReason);
      
      if (response.success) {
        const actionText = action === 'approve' ? 'approved' : 'rejected';
        const message = action === 'approve' 
          ? `YouTube media has been ${actionText} successfully!` 
          : `YouTube media has been ${actionText}${rejectionReason ? `: ${rejectionReason}` : ''}`;
        
        toast.success(message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Refresh the data
        if (campaignId && affiliateId) {
          await fetchYouTubeMedia();
        } else {
          await fetchApprovalRequests();
        }
        setApprovalModal({ isOpen: false, mediaId: null, videoId: null, campaignId: null, action: null, rejectionReason: '' });
      } else {
        const errorMessage = response.message || `Failed to ${action} YouTube media`;
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (err) {
      const errorMessage = `Failed to ${action} YouTube media. Please try again.`;
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error(`Error ${action}ing YouTube media:`, err);
    }
  };

  const openApprovalModal = (mediaId, videoId, campaignId, action) => {
    setApprovalModal({
      isOpen: true,
      mediaId,
      videoId,
      campaignId,
      action,
      rejectionReason: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-2 md:px-4 py-4 md:py-8">
        <div className="bg-red-100 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 md:px-4 py-4 md:py-8">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-2 md:px-4 py-1 md:py-2 border border-gray-300 rounded-lg text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200 mb-4"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>
        
        <h1 className="text-lg md:text-xl font-bold text-gray-800 mb-2">
          {campaignId && affiliateId ? 'YouTube Media Details' : 'YouTube Media Approval Requests'}
        </h1>
        <p className="text-xs md:text-sm text-gray-600 mb-4">
          {campaignId && affiliateId 
            ? 'Review and approve YouTube media submissions for this affiliate'
            : 'Review and approve YouTube media submissions from affiliates'
          }
        </p>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-wrap items-center space-x-2">
              <span className="text-xs md:text-sm font-medium text-gray-700">Filter by status:</span>
              <select
                value={statusFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs md:text-sm"
              >
                <option value="all">All ({statusCounts.all})</option>
                <option value="pending">Pending ({statusCounts.pending})</option>
                <option value="approved">Approved ({statusCounts.approved})</option>
                <option value="rejected">Rejected ({statusCounts.rejected})</option>
              </select>
              
              {activeFilters.length > 0 && (
                <button
                  onClick={resetFilters}
                  className="flex items-center px-2 md:px-3 py-1 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 text-xs md:text-sm"
                >
                  <FaFilter className="mr-2" />
                  Reset Filters
                </button>
              )}
            </div>
            
            <div className="text-xs md:text-sm text-gray-500">
              Showing {filteredMedia ? (campaignId && affiliateId ? filteredMedia.videos?.length || 0 : filteredMedia.approvalRequests?.length || 0) : 0} of {youtubeMedia ? (campaignId && affiliateId ? youtubeMedia.videos?.length || 0 : youtubeMedia.approvalRequests?.length || 0) : 0} media items
            </div>
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="mt-4 flex flex-wrap">
              {activeFilters.map((filter, index) => (
                <FilterChip
                  key={index}
                  label={`${filter.type}: ${filter.value}`}
                  onRemove={() => {
                    if (filter.type === 'status') {
                      handleFilterChange('all');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      ) : !filteredMedia ? (
        <div className="text-center py-12">
          <FaYoutube className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-500">No YouTube media data available.</p>
        </div>
      ) : campaignId && affiliateId ? (
        // Show specific campaign/affiliate YouTube media
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-2 md:p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm md:text-lg font-semibold text-gray-800">
                {filteredMedia.affiliate?.firstName && filteredMedia.affiliate?.lastName 
                  ? `${filteredMedia.affiliate.firstName} ${filteredMedia.affiliate.lastName}`
                  : filteredMedia.affiliate?.username || 'Unnamed Affiliate'}
              </h3>
              <span className="text-xs md:text-sm text-gray-500">{filteredMedia.affiliate?.email}</span>
            </div>
            
            {filteredMedia.videos && filteredMedia.videos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMedia.videos.map((video, videoIndex) => (
                  <div key={video.videoId || videoIndex} className="border rounded-lg p-2 md:p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(video.campaignStatus)}`}>
                        {video.campaignStatus}
                      </span>
                      {video.campaignStatus === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openApprovalModal(
                              filteredMedia.mediaId,
                              video.videoId,
                              campaignId,
                              'approve'
                            )}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => openApprovalModal(
                              filteredMedia.mediaId,
                              video.videoId,
                              campaignId,
                              'reject'
                            )}
                            className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <img
                      src={video.thumbnail || 'https://via.placeholder.com/120x90?text=No+Image'}
                      alt={video.title}
                      className="w-full h-24 md:h-32 object-cover rounded mb-2"
                    />
                    
                    <h5 className="font-medium text-xs md:text-sm mb-1 truncate" title={video.title}>
                      {video.title}
                    </h5>
                    
                    <div className="flex items-center gap-4 text-xs md:text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <FaEye /> {video.statistics?.viewCount || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaThumbsUp /> {video.statistics?.likeCount || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaComment /> {video.statistics?.commentCount || 0}
                      </span>
                    </div>
                    
                    {video.rejectionReason && (
                      <p className="text-xs md:text-sm text-red-600 mt-2">
                        <strong>Rejection Reason:</strong> {video.rejectionReason}
                      </p>
                    )}
                    
                    <a
                      href={video.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs md:text-sm"
                    >
                      Watch on YouTube
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaFilter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-sm md:text-lg font-medium text-gray-900 mb-2">No media found</h3>
                <p className="text-gray-500">
                  {statusFilter === 'all' 
                    ? 'No YouTube media submitted yet.'
                    : `No ${statusFilter} media found. Try adjusting your filters.`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      ) : filteredMedia?.approvalRequests?.length === 0 ? (
        <div className="text-center py-12">
          <FaYoutube className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-sm md:text-lg font-medium text-gray-900 mb-2">No Pending Approvals</h3>
          <p className="text-gray-500">
            {statusFilter === 'all' 
              ? 'There are no YouTube media approval requests at the moment.'
              : `No ${statusFilter} approval requests found. Try adjusting your filters.`
            }
          </p>
        </div>
      ) : (
        // Show general approval requests
        <div className="space-y-6">
          {filteredMedia.approvalRequests.map((request, index) => (
            <div key={`${request._id}-${request.video.videoId}-${index}`} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm md:text-lg font-semibold text-gray-800">
                    {request.user.firstName && request.user.lastName 
                      ? `${request.user.firstName} ${request.user.lastName}`
                      : request.user.username || 'Unnamed Affiliate'}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">{request.user.email}</p>
                  <p className="text-sm text-gray-500">Campaign: {request.campaign.name}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.approvalRequest.status)}`}>
                  {request.approvalRequest.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <img
                    src={request.video.thumbnail || 'https://via.placeholder.com/320x180?text=No+Image'}
                    alt={request.video.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-xs md:text-sm text-gray-800 mb-2">{request.video.title}</h4>
                    <p className="text-xs md:text-sm text-gray-600 line-clamp-3">{request.video.description}</p>
                  </div>

                  <div className="flex items-center gap-6 text-xs md:text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <FaEye /> {request.video.statistics?.viewCount || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <FaThumbsUp /> {request.video.statistics?.likeCount || 0} likes
                    </span>
                    <span className="flex items-center gap-1">
                      <FaComment /> {request.video.statistics?.commentCount || 0} comments
                    </span>
                  </div>

                  <div className="text-xs md:text-sm text-gray-500">
                    <p>Published: {request.video.publishedAt ? new Date(request.video.publishedAt).toLocaleDateString() : 'N/A'}</p>
                    <p>Requested: {request.approvalRequest.requestedAt ? new Date(request.approvalRequest.requestedAt).toLocaleDateString() : 'N/A'}</p>
                  </div>

                  <div className="flex gap-3">
                    <a
                      href={request.video.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2 md:px-4 py-1 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      <FaYoutube />
                      Watch on YouTube
                    </a>
                    
                    {request.approvalRequest.status === 'pending' && (
                      <>
                        <button
                          onClick={() => openApprovalModal(
                            request._id,
                            request.video.videoId,
                            request.campaign._id,
                            'approve'
                          )}
                          className="flex items-center gap-2 px-2 md:px-4 py-1 md:py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        >
                          <FaCheck />
                          Approve
                        </button>
                        <button
                          onClick={() => openApprovalModal(
                            request._id,
                            request.video.videoId,
                            request.campaign._id,
                            'reject'
                          )}
                          className="flex items-center gap-2 px-2 md:px-4 py-1 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                        >
                          <FaTimes />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval Modal */}
      {approvalModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-2 md:p-4 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {approvalModal.action === 'approve' ? 'Approve' : 'Reject'} YouTube Media
            </h3>
            
            {approvalModal.action === 'reject' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason (Optional)
                </label>
                <textarea
                  value={approvalModal.rejectionReason}
                  onChange={(e) => setApprovalModal(prev => ({ ...prev, rejectionReason: e.target.value }))}
                  className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Enter rejection reason..."
                />
              </div>
            )}
            
            <p className="text-xs md:text-sm text-gray-600 mb-6">
              Are you sure you want to {approvalModal.action} this YouTube media?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setApprovalModal({ isOpen: false, mediaId: null, videoId: null, campaignId: null, action: null, rejectionReason: '' })}
                className="px-2 md:px-4 py-1 md:py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className={`px-2 md:px-4 py-1 md:py-2 text-white rounded ${
                  approvalModal.action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              > 
                {approvalModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeMediaApproval; 